import { useEffect, useState } from "react";
import {
  Page,
  Card,
  IndexTable,
  Select,
  TextField,
  Pagination,
  BlockStack,
  Button,
  InlineStack,
  Spinner,
  Badge,
  Icon,
  Text,
} from "@shopify/polaris";
import { ChevronDownIcon, ChevronRightIcon } from "@shopify/polaris-icons";
import { api } from "../api";
import type { PrintJob } from "../types";
import StatusBadge from "../components/StatusBadge";
import EmptyState from "../components/EmptyState";
import FailureNotice from "../components/FailureNotice";
import { describePromiseSource } from "../lib/promise-source";

const STATUS_OPTIONS: Array<{ label: string; value: string }> = [
  { label: "All statuses", value: "" },
  { label: "Queued", value: "queued" },
  { label: "Pending", value: "pending" },
  { label: "Label purchased", value: "label_purchased" },
  { label: "Printed", value: "printed" },
  { label: "Failed", value: "failed" },
  { label: "Bought, not printed", value: "print_failed" },
  { label: "No rule matched", value: "no_rule_match" },
  { label: "Skipped", value: "skipped" },
];

const PAGE_SIZE = 20;

interface PrintJobsResponse {
  jobs: PrintJob[];
  total: number;
  page: number;
  page_size: number;
}

// A slip normally ships inside its label's job as page 1, so there is no
// separate status to report — the label's own status covers both. Older rows,
// and any order whose merge fell back to two jobs, still carry a child job
// whose outcome is joined on and shown here.
function PackingListCell({ job }: { job: PrintJob }): JSX.Element {
  if (job.pages === "packing_list+label") {
    return <Badge tone="success">Same job</Badge>;
  }
  if (job.packing_list_status) {
    return (
      <Badge tone={job.packing_list_status === "printed" ? "success" : "critical"}>
        {job.packing_list_status === "printed" ? "Printed" : "Failed"}
      </Badge>
    );
  }
  return <>—</>;
}

function formatDeadline(iso: string): string {
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? iso : parsed.toLocaleDateString();
}

// Why ShipPromise bought the rate it bought. Only the merchant can tell whether
// the deadline it read was the right one, so the field it came from is named
// rather than summarised away.
function ShippingDecision({ job }: { job: PrintJob }): JSX.Element | null {
  if (!job.promise_source) return null;

  const days = job.promise_max_delivery_days;
  const eligible = job.eligible_rate_count;
  const total = job.total_rate_count;

  return (
    <BlockStack gap="100">
      <InlineStack gap="200" blockAlign="center">
        <Text as="span" fontWeight="semibold">
          Shipping decision
        </Text>
        <Badge>{describePromiseSource(job.promise_source)}</Badge>
        {job.promise_hard_deadline ? <Badge tone="attention">Contractual</Badge> : null}
        {job.promise_met === 0 ? <Badge tone="critical">No rate met the deadline</Badge> : null}
      </InlineStack>
      <Text as="p" tone="subdued" variant="bodySm">
        {job.promise_deadline_date ? `Deliver by ${formatDeadline(job.promise_deadline_date)}` : "No deadline"}
        {days !== null && days !== undefined ? ` (${days} day${days === 1 ? "" : "s"})` : ""}
        {" · "}
        {eligible !== null && eligible !== undefined && total !== null && total !== undefined
          ? `${eligible} of ${total} rate${total === 1 ? "" : "s"} could meet it`
          : "Rate counts not recorded"}
        {job.selected_delivery_days !== null && job.selected_delivery_days !== undefined
          ? ` · bought a ${job.selected_delivery_days}-day service`
          : ""}
        {job.cheapest_rate_cents !== null && job.cheapest_rate_cents !== undefined
          ? ` · cheapest quoted $${(job.cheapest_rate_cents / 100).toFixed(2)}`
          : ""}
      </Text>
    </BlockStack>
  );
}

export default function Activity(): JSX.Element {
  const [jobs, setJobs] = useState<PrintJob[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [trackingSearch, setTrackingSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [expandedJobId, setExpandedJobId] = useState<number | null>(null);
  const [reprintingId, setReprintingId] = useState<number | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    setLoading(true);
    setExpandedJobId(null);
    const params = new URLSearchParams({ page: String(page), page_size: String(PAGE_SIZE) });
    if (status) params.set("status", status);
    if (trackingSearch) params.set("tracking", trackingSearch);

    api
      .get<PrintJobsResponse>(`/print-jobs?${params.toString()}`)
      .then((response) => {
        setJobs(response.jobs);
        setTotal(response.total);
      })
      .finally(() => setLoading(false));
  }, [page, status, trackingSearch, reload]);

  // Re-sends postage the merchant has already paid for. Never buys.
  async function reprint(jobId: number): Promise<void> {
    setReprintingId(jobId);
    try {
      await api.post(`/print-jobs/${jobId}/reprint`);
      setReload((n) => n + 1);
    } finally {
      setReprintingId(null);
    }
  }

  const totalPages = Math.max(Math.ceil(total / PAGE_SIZE), 1);

  const rows: JSX.Element[] = [];
  for (const job of jobs) {
    const expandable =
      Boolean(job.error) ||
      Boolean(job.skip_reason) ||
      Boolean(job.packing_list_error) ||
      Boolean(job.promise_source);
    const expanded = expandedJobId === job.id;
    // Only meaningful once a label was bought — before that carrier is null
    // and every row would look like a fallback.
    const otherCarrier =
      Boolean(job.carrier) && Boolean(job.rule_preferred_carrier) && job.carrier !== job.rule_preferred_carrier;

    rows.push(
      <IndexTable.Row
        id={String(job.id)}
        key={job.id}
        position={rows.length}
        onClick={expandable ? () => setExpandedJobId(expanded ? null : job.id) : undefined}
      >
        <IndexTable.Cell>{job.shopify_order_name ?? job.shopify_order_id}</IndexTable.Cell>
        <IndexTable.Cell>
          <InlineStack gap="100" blockAlign="center">
            <StatusBadge status={job.status} />
            {expandable ? <Icon source={expanded ? ChevronDownIcon : ChevronRightIcon} tone="subdued" /> : null}
          </InlineStack>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <InlineStack gap="100" blockAlign="center">
            <span>{job.carrier ?? "—"}</span>
            {otherCarrier ? <Badge tone="attention">Fallback</Badge> : null}
          </InlineStack>
        </IndexTable.Cell>
        <IndexTable.Cell>{job.service_level ?? "—"}</IndexTable.Cell>
        <IndexTable.Cell>{job.tracking_number ?? "—"}</IndexTable.Cell>
        {/* A zero-cost label is a real answer, so it must not read as "no data". */}
        <IndexTable.Cell>
          {job.cost_cents === null || job.cost_cents === undefined
            ? "—"
            : `$${(job.cost_cents / 100).toFixed(2)}`}
        </IndexTable.Cell>
        <IndexTable.Cell>
          <PackingListCell job={job} />
        </IndexTable.Cell>
        <IndexTable.Cell>{new Date(job.created_at).toLocaleString()}</IndexTable.Cell>
      </IndexTable.Row>
    );

    if (expandable && expanded) {
      rows.push(
        <IndexTable.Row id={`${job.id}-error`} key={`${job.id}-error`} position={rows.length}>
          <IndexTable.Cell colSpan={8}>
            <BlockStack gap="200">
              {/* Not an error: ShipPromise correctly bought nothing. Shown plainly. */}
              {job.skip_reason ? (
                <Text as="p" tone="subdued" breakWord>
                  {job.skip_reason}
                </Text>
              ) : null}
              {job.error ? (
                <FailureNotice error={job.error} detail={job.error_detail} variant="bodyMd" />
              ) : null}
              {job.status === "print_failed" ? (
                <InlineStack gap="200" blockAlign="center">
                  <Text as="span" tone="subdued" variant="bodySm">
                    This postage is bought and paid for. Reprinting costs nothing.
                  </Text>
                  <Button
                    size="slim"
                    loading={reprintingId === job.id}
                    disabled={reprintingId !== null}
                    onClick={() => {
                      void reprint(job.id);
                    }}
                  >
                    Reprint label
                  </Button>
                  {job.label_url ? (
                    <Button size="slim" variant="plain" url={job.label_url} target="_blank">
                      Download label
                    </Button>
                  ) : null}
                </InlineStack>
              ) : null}
              {job.packing_list_error ? (
                <Text as="p" tone="critical" breakWord>
                  Packing list: {job.packing_list_error}
                </Text>
              ) : null}
              <ShippingDecision job={job} />
            </BlockStack>
          </IndexTable.Cell>
        </IndexTable.Row>
      );
    }
  }

  return (
    <Page title="Activity" subtitle="Full print job audit log">
      <Card>
        <InlineStack gap="300">
          <div style={{ minWidth: 220 }}>
            <Select
              label="Status"
              labelHidden
              options={STATUS_OPTIONS}
              value={status}
              onChange={(value) => {
                setStatus(value);
                setPage(1);
              }}
            />
          </div>
          <div style={{ minWidth: 260 }}>
            <TextField
              label="Search tracking number"
              labelHidden
              placeholder="Search tracking number"
              value={trackingSearch}
              onChange={(value) => {
                setTrackingSearch(value);
                setPage(1);
              }}
              autoComplete="off"
            />
          </div>
        </InlineStack>
      </Card>

      {loading ? (
        <Spinner accessibilityLabel="Loading activity" size="large" />
      ) : jobs.length === 0 ? (
        <EmptyState heading="No print jobs match these filters" />
      ) : (
        <Card padding="0">
          <IndexTable
            resourceName={{ singular: "print job", plural: "print jobs" }}
            itemCount={jobs.length}
            headings={[
              { title: "Order" },
              { title: "Status" },
              { title: "Carrier" },
              { title: "Service" },
              { title: "Tracking" },
              { title: "Cost" },
              { title: "Packing list" },
              { title: "Created" },
            ]}
            selectable={false}
          >
            {rows}
          </IndexTable>
          <div style={{ display: "flex", justifyContent: "center", padding: "1rem" }}>
            <Pagination
              hasPrevious={page > 1}
              onPrevious={() => setPage((p) => p - 1)}
              hasNext={page < totalPages}
              onNext={() => setPage((p) => p + 1)}
              label={`Page ${page} of ${totalPages}`}
            />
          </div>
        </Card>
      )}
    </Page>
  );
}
